/**
 * Characters a first name may contain: letters, the accent marks that attach to
 * them, hyphens and apostrophes.
 *
 * Everything else is refused — digits, dots, commas, spaces, symbols. A name is
 * not a free-text field, and letting one carry "Patrick2" or "Patrick," means
 * that string turns up on an invoice, in an email greeting and on a licence
 * certificate.
 *
 * `\p{M}` matters as much as `\p{L}`: it covers combining accent marks. Without
 * it, an "é" typed as "e" + a combining acute has its accent stripped and the
 * customer watches their own name lose it as they type.
 *
 * Both apostrophes are allowed — the straight one and the curly one a phone
 * keyboard produces automatically.
 */
// Two regexes, not one shared with the `g` flag. A global regex carries
// `lastIndex` between calls, so `.test()` on the same pattern returns
// true, false, true, false for identical input — the field would shake on
// every other rejected keystroke and look broken rather than strict.
const DISALLOWED_IN_NAME = /[^\p{L}\p{M}'’-]/u;
const DISALLOWED_IN_NAME_GLOBAL = /[^\p{L}\p{M}'’-]/gu;

/** True when the value contains something a name may not carry. */
export const hasDisallowedNameCharacters = (value: string): boolean => DISALLOWED_IN_NAME.test(value);

/** Remove anything a name may not carry, keeping the rest in order. */
export const stripNonNameCharacters = (value: string): string => value.replace(DISALLOWED_IN_NAME_GLOBAL, "");

/**
 * Normalise a personal name to "first letter capital, the rest lower".
 *
 *   "PATRICK"     → "Patrick"
 *   "patrick"     → "Patrick"
 *   "anne-marie"  → "Anne-Marie"
 *   "ANNE-MARIE"  → "Anne-Marie"
 *   "o'brien"     → "O'Brien"
 *
 * This is the courtesy copy. The SERVER applies the identical rule in
 * `common/validation/normalize-name.ts`, and that one is the enforcement:
 * signing up from anywhere that is not this form still produces "Patrick".
 * Keep the two in step — this exists only so the customer sees it happen while
 * typing rather than discovering it afterwards.
 *
 * Names are corrected, never rejected. Someone typing their own name in
 * capitals has not made an error worth an error message.
 *
 * Apostrophes are treated like hyphens: without that, "O'Brien" would be stored
 * as "O'brien", which is a name spelled wrong — a worse outcome than the
 * shouting the rule exists to fix.
 *
 * Never changes the LENGTH of the value, which is what makes it safe to run on
 * every keystroke: the caret stays where the customer put it. (Stripping is a
 * separate step, and it removes the character just typed — so the caret staying
 * at the end is the correct result there.)
 */
export const normalizePersonName = (value: string): string =>
  value
    .toLocaleLowerCase()
    .replace(/(^|[-'’])(\p{L})/gu, (_match, boundary: string, letter: string) => boundary + letter.toLocaleUpperCase());
